import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Lottie from 'lottie-react';
import cx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { IconRcTick } from '@/assets';
import { IconRcBadgeMintLoading } from '@/assets/badge';
import { BadgeAvatar, HelperTooltip, Modal } from '@/components';
import { useCurrentAddressInfo, useSwitch, useUserInfo } from '@/hooks';
import { login } from '@/utils';

import {
  useBadgeCanClaim,
  useBadgeCanMinted,
  useBadgeDetail,
  useBadgeHasMinted,
  useBadgeMint,
  useClaimCode,
  useIsOfficialBadge,
} from '../../hooks';
import badgeMintAnimation from './badge-mint.json';
import style from './index.module.scss';
import { ReCaptchaModal } from '@/components/ReCaptchaModal';
import { ClaimCodeModal } from '../ClaimCodeModal';
import { OfficialBadge } from '../../types';

const { CommonModal } = Modal;

export const MintBadgeButton = ({
  id,
  badgeLogo,
  mintTime = 0,
}: {
  id: string;
  badgeLogo?: string;
  mintTime?: number;
}) => {
  const { t } = useTranslation();

  const { address, isVerified } = useCurrentAddressInfo();
  const isOfficialBadge = useIsOfficialBadge();
  const { data: userInfo } = useUserInfo(address);
  const { data: badgeData } = useBadgeDetail(id);
  const isClaimCode = isOfficialBadge && (badgeData as OfficialBadge)?.mint_type === 'claim_code';

  const { data: canMint, isLoading: isCanMintLoading } = useBadgeCanMinted({
    id,
    enabled: !!badgeData && !isClaimCode,
    isOfficial: isOfficialBadge,
  });
  const { data: canClaim, isLoading: isCanClaimLoading } = useBadgeCanClaim(id, isClaimCode);
  const { data: claimCode } = useClaimCode({ badgeId: id, enabled: isClaimCode });
  const { data: mintInfo, isLoading: isMintedLoading } = useBadgeHasMinted(id);
  const { mutateAsync: mintBadge, isLoading } = useBadgeMint({ id, isOfficial: isOfficialBadge });
  const [mintId, setMintId] = useState<number>();
  const { on, turnOn, turnOff } = useSwitch();
  const {
    on: recaptchaModalVisible,
    turnOn: showRecaptchaModal,
    turnOff: hideRecaptchaModal,
  } = useSwitch(false);
  const {
    on: claimCodeModal,
    turnOn: showClaimCodeModal,
    turnOff: hideClaimCodeModal,
  } = useSwitch();
  const [token, setToken] = useState<string>();

  const needRecaptcha = useMemo(() => {
    if (isClaimCode) {
      return !userInfo?.desc?.user?.web3_id && !claimCode;
    }
    return !userInfo?.desc?.user?.web3_id;
  }, [claimCode, isClaimCode, userInfo?.desc?.user?.web3_id]);

  const mintedDate = useMemo(() => {
    return mintInfo?.has_minted ? dayjs.unix(mintInfo!.mint_at).utc().format('YYYY/MM/DD') : '';
  }, [mintInfo]);

  const isBtnLoading = useMemo(
    () => isMintedLoading || isCanMintLoading || isCanClaimLoading,
    [isCanClaimLoading, isCanMintLoading, isMintedLoading],
  );

  const btnCanMint = useMemo(
    () => canMint || canClaim || !isVerified || claimCode,
    [canClaim, canMint, claimCode, isVerified],
  );

  const handleMintClick = useCallback(
    async (token?: string) => {
      if (!isVerified) {
        login();

        return;
      }

      if (needRecaptcha && !token) {
        showRecaptchaModal();
        return;
      }

      if (isClaimCode) {
        showClaimCodeModal();
        return;
      }

      const data = await mintBadge(token || undefined);
      if (data.is_success) {
        setMintId(data.inner_id!);
        turnOn();
      }
    },
    [
      isVerified,
      isClaimCode,
      needRecaptcha,
      mintBadge,
      showClaimCodeModal,
      showRecaptchaModal,
      turnOn,
    ],
  );

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      if (token) {
        hideRecaptchaModal();
        setToken(token);
        handleMintClick(token);
      }
    },
    [handleMintClick, hideRecaptchaModal],
  );

  const disabledTooltip = useMemo(() => {
    return isBtnLoading || btnCanMint
      ? ''
      : new Date().getTime() / 1000 < mintTime
        ? t('MintBadgeButton.notStart')
        : isClaimCode
          ? t('MintBadgeButton.rabbydisabledtooltip', { limit: '$10' })
          : t('MintBadgeButton.not-eligible');
  }, [btnCanMint, isBtnLoading, isClaimCode, mintTime, t]);

  return (
    <>
      <div className={style.container}>
        <ReCaptchaModal
          on={recaptchaModalVisible}
          turnOff={hideRecaptchaModal}
          onChange={handleRecaptchaChange}
          action="MINT_BADGE"
        />
        {isBtnLoading ? (
          <Skeleton className={style.skeleton} />
        ) : mintInfo?.has_minted ? (
          <div className={cx(style.mintBtnWrap, style.minted)}>
            <IconRcTick color="var(--color-greeen)" />
            <div className={style.mintedInfo}>
              <div className={style.mintedTitle}>{t('MintBadgeButton.1247zq9')}</div>
              <div className={style.mintedDate}>{mintedDate}</div>
            </div>
          </div>
        ) : btnCanMint ? (
          <div
            className={cx(style.mintBtnWrap, isLoading && style.minting)}
            onClick={() => handleMintClick()}>
            <div className={style.mintable}>
              <IconRcBadgeMintLoading className={style.mintIcon} />
              <span className={style.mintTitle}>{t('MintBadgeButton.1feki')}</span>
            </div>
            <div className={style.mintableTopLayer}>
              <div className={style.mintableTopInner}>
                <IconRcBadgeMintLoading className={style.mintIcon} />
                <span className={style.mintTitle}>{t('MintBadgeButton.1feki')}</span>
              </div>
            </div>
          </div>
        ) : (
          <HelperTooltip title={disabledTooltip}>
            <div className={cx(style.mintBtnWrap, style.noObtained)}>
              <IconRcBadgeMintLoading className={style.mintIcon} />
              <span>{t('MintBadgeButton.1feki')}</span>
            </div>
          </HelperTooltip>
        )}
      </div>
      {mintId && on ? (
        <MintSuccessModal mintId={mintId} on={on} badgeLogo={badgeLogo} turnOff={turnOff} />
      ) : null}
      {badgeData && (
        <ClaimCodeModal
          on={claimCodeModal}
          turnOff={hideClaimCodeModal}
          token={token}
          claimCode={claimCode}
          data={badgeData as OfficialBadge}
        />
      )}
    </>
  );
};

const MintSuccessModal = memo(
  ({
    mintId,
    badgeLogo,
    on,
    turnOff,
  }: {
    mintId: number;
    badgeLogo?: string;
    on: boolean;
    turnOff: () => void;
  }) => {
    const { t } = useTranslation();

    return (
      <>
        <CommonModal containerClassName={style.successModal} on={on} turnOff={turnOff}>
          <BadgeAvatar src={badgeLogo} className={style.badgeLogo} width={140} />
          <div className={style.successModalTitle}>{t('MintBadgeButton.17btsf0')}</div>
          <div className={style.successModalDesc}>
            {t('MintBadgeButton.wubunb')}
            <span className={style.successMintId}>#{mintId}</span>
            {t('MintBadgeButton.1tjh45v')}
          </div>
        </CommonModal>
        <Lottie
          animationData={badgeMintAnimation}
          className={style.successLottie}
          onClick={turnOff}
          loop={false}
        />
      </>
    );
  },
);
